import React, { useContext, useEffect, useState } from 'react'

import withView from 'core/components/withView'
import useFetch from 'core/hooks/use-fetch'
import DynamiteList from 'dynamite/components/DynamiteList'
import { GlobalContext } from 'core/contexts/global-context'
import { getPostBackgrounds } from 'core/helpers/wp-data-helpers'
import config from 'config'

const TARGET_ATTACHMENTS = 30
const TARGET_POSTS = 10
const PARALLEL_PULLS = 3

const decodeHtml = (html) => {
  if (!html) return ''
  const t = document.createElement('textarea')
  t.innerHTML = html
  return t.value
}

// Browsers can't render TIFF in <img> tags. WP keeps the .tif extension on
// generated thumbnails, so any source URL ending in .tif/.tiff would render
// as a broken image.
const isTiffAttachment = (item) => {
  if (!item || !item.sources) return false
  return Object.values(item.sources).some(src => {
    const url = Array.isArray(src) ? src[0] : null
    return typeof url === 'string' && /\.tiff?($|\?)/i.test(url)
  })
}

const shuffle = (arr) => {
  const out = arr.slice()
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

const Dynamite = (props) => {
  const global = useContext(GlobalContext)
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)

  useFetch('/page/dynamite', {
    onSuccess: (data) => {
      global.setBackground(getPostBackgrounds(data))
    }
  })

  useEffect(() => {
    let cancelled = false
    const wpV1 = config.apiUrl                       // .../wp-json/v1
    const wpBase = wpV1.replace(/\/v1$/, '')         // .../wp-json

    const fetchJson = (url) => fetch(url).then(r => r.json())

    const run = async () => {
      try {
        // 1. Pull /v1/dynamite multiple times in parallel for a bigger sample.
        const pulls = await Promise.all(
          Array.from({ length: PARALLEL_PULLS }, () => fetchJson(`${wpV1}/dynamite`))
        )

        // 2. Dedupe by ID across all pulls.
        const byId = new Map()
        pulls.forEach(p => {
          if (p && p.success && Array.isArray(p.data)) {
            p.data.forEach(item => { if (!byId.has(item.ID)) byId.set(item.ID, item) })
          }
        })
        const allItems = [...byId.values()]
        const allAttachments = allItems.filter(i => i.post_type === 'attachment')
        const allPosts = allItems.filter(i => i.post_type !== 'attachment')

        // 3. Pre-filter attachments we already know are bad (TIFF).
        const nonTiffAttachments = allAttachments.filter(a => !isTiffAttachment(a))

        // 4. Look up parents in one batched call.
        const ids = nonTiffAttachments.map(a => a.ID)
        let parentByAttachment = new Map()
        if (ids.length > 0) {
          // /wp/v2/media caps per_page at 100, paginate if needed.
          const pages = []
          for (let i = 0; i < ids.length; i += 100) {
            const slice = ids.slice(i, i + 100)
            pages.push(fetchJson(`${wpBase}/wp/v2/media?include=${slice.join(',')}&per_page=100&_fields=id,post`))
          }
          const mediaResults = await Promise.all(pages)
          mediaResults.forEach(media => {
            if (Array.isArray(media)) {
              media.forEach(m => parentByAttachment.set(m.id, m.post || null))
            }
          })
        }

        // 5. Keep only attachments with a confirmed parent.
        const goodAttachments = nonTiffAttachments.filter(a => parentByAttachment.get(a.ID))

        // 6. Shuffle and trim to targets.
        const chosenAttachments = shuffle(goodAttachments).slice(0, TARGET_ATTACHMENTS)
        const chosenPosts = shuffle(allPosts).slice(0, TARGET_POSTS)

        // 7. Replace "unassigned-XXX" titles with their parent post's title.
        const needsParentTitle = chosenAttachments.filter(item =>
          /^unassigned-/i.test(item.post_title || '')
        )
        if (needsParentTitle.length > 0) {
          const parentIds = [...new Set(
            needsParentTitle.map(i => parentByAttachment.get(i.ID))
          )]
          try {
            const parents = await fetchJson(
              `${wpBase}/wp/v2/search?include=${parentIds.join(',')}&subtype=any&per_page=100&_fields=id,title`
            )
            const titleById = new Map()
            if (Array.isArray(parents)) {
              parents.forEach(p => titleById.set(p.id, decodeHtml(p.title)))
            }
            needsParentTitle.forEach(item => {
              const parentTitle = titleById.get(parentByAttachment.get(item.ID))
              if (parentTitle) item.post_title = parentTitle
            })
          } catch (_) { /* non-fatal */ }
        }

        if (cancelled) return
        setItems(shuffle([...chosenAttachments, ...chosenPosts]))
      } catch (err) {
        if (!cancelled) setError(err)
      }
    }

    run()
    return () => { cancelled = true }
  }, [])

  if (error) throw new Error(error.message || 'Dynamite load failed')
  if (!items) return null

  return (
    <div className={props.className}>
      <DynamiteList items={items} />
    </div>
  )
}

export default withView(Dynamite)
